/**
 * OVERRIDE reducers/querystringsearch/querystringsearch.js
 * REASON: Do not load on first render if data is already loaded from SSR to avoid flickering.
 * FILE:
 * FILE VERSION: VLT 5.0.1
 * PULL REQUEST:
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/893
 * DATE: 2025-10-27
 * DEVELOPER: @reebalazs
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */

import map from 'lodash/map';
import omit from 'lodash/omit';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

// START CUSTOMIZATION
import { QUERYSTRING_CLEAR_SSR } from '../../actions/querystringsearch/querystringsearch';
// END CUSTOMIZATION
const GET_QUERYSTRING_RESULTS = 'GET_QUERYSTRING_RESULTS';
const RESET_QUERYSTRING_RESULTS = 'RESET_QUERYSTRING_RESULTS';

const initialState = {
  error: null,
  items: [],
  total: 0,
  loaded: false,
  loading: false,
  batching: {},
  subrequests: {},
};

// START CUSTOMIZATION
const isStale = (state, action) =>
  !!action.queryKey &&
  !!state.subrequests?.[action.subrequest]?.queryKey &&
  state.subrequests[action.subrequest].queryKey !== action.queryKey;
// END CUSTOMIZATION

/**
 * querystringsearch reducer.
 * @function querystring
 * @param {Object} state Current state.
 * @param {Object} action Action to be handled.
 * @returns {Object} New state.
 */
export default function querystringsearch(state = initialState, action = {}) {
  switch (action.type) {
    case `${GET_QUERYSTRING_RESULTS}_PENDING`:
      return action.subrequest
        ? {
            ...state,
            subrequests: {
              ...state.subrequests,
              [action.subrequest]: {
                ...(state.subrequests[action.subrequest] || {
                  items: [],
                  total: 0,
                  batching: {},
                }),
                items: [],
                total: 0,
                error: null,
                loaded: false,
                loading: true,
                // START CUSTOMIZATION
                queryKey: action.queryKey,
                // END CUSTOMIZATION
              },
            },
          }
        : {
            ...state,
            error: null,
            loading: true,
            loaded: false,
          };
    case `${GET_QUERYSTRING_RESULTS}_SUCCESS`:
      // START CUSTOMIZATION
      return isStale(state, action)
        ? state
        : // END CUSTOMIZATION
          action.subrequest
          ? {
              ...state,
              subrequests: {
                ...state.subrequests,
                [action.subrequest]: {
                  error: null,
                  items: map(action.result.items, (item) => ({
                    ...item,
                    '@id': flattenToAppURL(item['@id']),
                  })),
                  total: action.result.items_total,
                  loaded: true,
                  loading: false,
                  batching: { ...action.result.batching },
                  // START CUSTOMIZATION
                  isSSR: action.isSSR,
                  queryKey: action.queryKey,
                  // END CUSTOMIZATION
                },
              },
            }
          : {
              ...state,
              error: null,
              items: map(action.result.items, (item) => ({
                ...item,
                '@id': flattenToAppURL(item['@id']),
              })),
              total: action.result.items_total,
              loaded: true,
              loading: false,
              batching: { ...action.result.batching },
              // START CUSTOMIZATION
              isSSR: action.isSSR,
              // END CUSTOMIZATION
            };
    case `${GET_QUERYSTRING_RESULTS}_FAIL`:
      // START CUSTOMIZATION
      return isStale(state, action)
        ? state
        : // END CUSTOMIZATION
          action.subrequest
          ? {
              ...state,
              subrequests: {
                ...state.subrequests,
                [action.subrequest]: {
                  error: action.error,
                  items: [],
                  total: 0,
                  loading: false,
                  loaded: false,
                  batching: {},
                  // START CUSTOMIZATION
                  queryKey: action.queryKey,
                  // END CUSTOMIZATION
                },
              },
            }
          : {
              ...state,
              error: action.error,
              items: [],
              total: 0,
              loading: false,
              loaded: false,
              batching: {},
            };
    case RESET_QUERYSTRING_RESULTS:
      return action.subrequest
        ? {
            ...state,
            subrequests: omit(state.subrequests, [action.subrequest]),
          }
        : {
            ...state,
            error: null,
            items: [],
            total: 0,
            loading: false,
            loaded: false,
            batching: {},
          };
    // START CUSTOMIZATION
    case QUERYSTRING_CLEAR_SSR:
      return action.subrequest
        ? {
            ...state,
            subrequests: {
              ...state.subrequests,
              [action.subrequest]: {
                ...state.subrequests[action.subrequest],
                isSSR: false,
              },
            },
          }
        : {
            ...state,
            isSSR: false,
          };
    // END CUSTOMIZATION
    default:
      return state;
  }
}
